import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RCta } from '../RCta';
import { RIcon } from '../RIcon';
import { ROpt } from '../ROpt';
import { WCta } from '../WCta';
import { WregPhase } from '../WregPhase';
import { WREG_WORKING_MS } from '../wregSteps';
import type { WregDisclosure, WregOfferedTrack, WregTrackId } from '../wregScenarios';
import {
  WREG_CONDITIONS,
  WREG_TRACK_CONDITIONS,
  WREG_TRACK_SUBCONDITIONS,
  type WregConditionId,
  type WregSubConditionId,
} from './wregTracksData';

type SubSelection = Partial<Record<WregTrackId, Partial<Record<WregSubConditionId, boolean>>>>;

type Props = {
  offered: WregOfferedTrack[];
  blocked?: WregTrackId;
  gate?: boolean;
  disclosure: WregDisclosure;
  sub?: 'confirm' | null;
  notice?: string | null;
  initial?: WregTrackId[];
  onContinue: (ids: WregTrackId[], detail: SubSelection | Partial<Record<WregConditionId, boolean>>) => void;
  onNoCondition: () => void;
};

type CheckRowProps = {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
};

const WregCheckRow = ({ on, onClick, children }: CheckRowProps) => (
  <label className={`c-checkrow${on ? ' on' : ''}`} onClick={onClick}>
    <span className="box">
      <RIcon.check />
    </span>
    {children}
  </label>
);

type TrackOptProps = {
  track: WregOfferedTrack;
  on: boolean;
  disabled: boolean;
  onToggle: () => void;
  subs: WregSubConditionId[] | null;
  subSel?: Partial<Record<WregSubConditionId, boolean>>;
  onSub: (id: WregSubConditionId) => void;
  bare: boolean;
  ctl: boolean;
  radio: boolean;
};

const WregTrackOpt = ({
  track,
  on,
  disabled,
  onToggle,
  subs,
  subSel,
  onSub,
  bare,
  ctl,
  radio,
}: TrackOptProps) => {
  const { t } = useTranslation();
  const shell = bare ? 'wreg-trackrow' : 'c-checkopt wreg-trackopt';
  return (
    <div
      className={`${shell}${on ? ' on' : ''}${disabled ? ' is-disabled' : ''}${ctl ? ' is-ctl' : ''}`}
    >
      <button type="button" className="wreg-trackopt-head" disabled={disabled} onClick={onToggle}>
        {radio ? (
          <span className="ring" />
        ) : (
          <span className="c-checkopt-box">
            <RIcon.check />
          </span>
        )}
        <span className="c-checkopt-body">
          <span className="c-checkopt-title">{t(`wreg.tracks.${track.id}.t`)}</span>
          {ctl ? (
            <span className="wreg-trackopt-ctl">
              <span className="wreg-trackopt-ctl-t">{t('wreg.tracks.ctl.title')}</span>
              <span className="wreg-trackopt-ctl-b">{t('wreg.tracks.ctl.body')}</span>
            </span>
          ) : (
            <span className="c-checkopt-desc">{t(`wreg.tracks.${track.id}.d`)}</span>
          )}
        </span>
      </button>
      {subs && (
        <div className="wreg-trackopt-sub">
          <div className="wreg-trackopt-q">{t('wreg.tracks.subq')}</div>
          <div className="c-checkgroup">
            {subs.map((id) => (
              <WregCheckRow key={id} on={Boolean(subSel && subSel[id])} onClick={() => onSub(id)}>
                {t(`wreg.subcond.${id}`)}
              </WregCheckRow>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const WregTracks = ({
  offered,
  blocked,
  gate,
  disclosure,
  sub,
  notice,
  initial,
  onContinue,
  onNoCondition,
}: Props) => {
  const { t } = useTranslation();
  const [sel, setSel] = useState<Partial<Record<WregTrackId, boolean>>>(() => {
    const seed =
      initial && initial.length
        ? initial
        : offered[0] && !offered[0].pending
          ? [offered[0].id]
          : [];
    return Object.fromEntries(seed.map((id) => [id, true]));
  });
  const [subSel, setSubSel] = useState<SubSelection>({});
  const [phase, setPhase] = useState<'select' | 'confirm'>(sub ?? 'select');
  const [conds, setConds] = useState<Partial<Record<WregConditionId, boolean>>>({});
  const [openCms, setOpenCms] = useState(false);

  useEffect(() => {
    if (sub) setPhase(sub);
  }, [sub]);

  const qualifies = (track: WregOfferedTrack) =>
    Object.values(subSel[track.id] ?? {}).some(Boolean);
  const picked = offered.filter((track) => sel[track.id]);
  const pending = picked.filter((track) => track.pending);

  if (phase === 'confirm') {
    const ids = new Set<WregConditionId>();
    (pending.length ? pending : picked).forEach((track) =>
      WREG_TRACK_CONDITIONS[track.id].forEach((c) => ids.add(c)),
    );
    const list = WREG_CONDITIONS.filter((c) => (ids.size ? ids.has(c) : true));
    const any = Object.values(conds).some(Boolean);
    const submit = () => onContinue(picked.map((track) => track.id), conds);
    return (
      <div className="rg-screen">
        <div className="rg-inner rg-inner-head">
          <div className="rg-head-block">
            <WregPhase n={2} />
            <h1 className="rg-h1">{t('wreg.confirm.title')}</h1>
            <p className="rg-sub">{t('wreg.confirm.sub')}</p>
          </div>
        </div>
        <div className="rg-list-wrap">
          <div className="rg-list">
            <div className="rg-list-inner rg-opts">
              {notice && (
                <div className="rg-disclosure">
                  <span className="ic" aria-hidden="true">i</span>
                  <p>{notice}</p>
                </div>
              )}
              {list.map((c) => (
                <ROpt
                  key={c}
                  on={Boolean(conds[c])}
                  onClick={() => setConds((p) => ({ ...p, [c]: !p[c] }))}
                  title={t(`wreg.cond.${c}`)}
                />
              ))}
              <ROpt dashed on={false} onClick={onNoCondition} title={t('wreg.confirm.none')} />
            </div>
          </div>
        </div>
        <div className="rg-foot">
          {sub === 'confirm' ? (
            <WCta disabled={!any} minWorkingMs={WREG_WORKING_MS} onDone={submit}>
              {t('wreg.confirm.cta')}
            </WCta>
          ) : (
            <RCta disabled={!any} onClick={submit}>
              {t('wreg.confirm.cta')}
            </RCta>
          )}
        </div>
      </div>
    );
  }

  const rowOf = (track: WregOfferedTrack, exclusiveWith?: WregTrackId[]) => {
    const subs = track.pending ? WREG_TRACK_SUBCONDITIONS[track.id] : null;
    return (
      <WregTrackOpt
        key={track.id}
        track={track}
        subs={track.ctl ? null : subs}
        subSel={subSel[track.id]}
        bare={Boolean(exclusiveWith)}
        ctl={Boolean(track.ctl)}
        radio={Boolean(exclusiveWith)}
        on={!track.ctl && Boolean(sel[track.id])}
        disabled={Boolean(track.ctl) || Boolean(subs && !qualifies(track))}
        onToggle={() =>
          setSel((p) => {
            const next = { ...p, [track.id]: exclusiveWith ? true : !p[track.id] };
            if (exclusiveWith && next[track.id]) {
              exclusiveWith.forEach((id) => {
                next[id] = false;
              });
              setSubSel((q) => {
                const r = { ...q };
                exclusiveWith.forEach((id) => {
                  r[id] = {};
                });
                return r;
              });
            }
            return next;
          })
        }
        onSub={(id) => {
          const nextChecks = { ...(subSel[track.id] ?? {}), [id]: !(subSel[track.id] ?? {})[id] };
          setSubSel((p) => ({ ...p, [track.id]: nextChecks }));
          const ok = Object.values(nextChecks).some(Boolean);
          setSel((p) => {
            const next = { ...p, [track.id]: ok };
            if (exclusiveWith && ok) {
              exclusiveWith.forEach((x) => {
                next[x] = false;
              });
            }
            return next;
          });
          if (exclusiveWith && ok) {
            setSubSel((q) => {
              const r = { ...q };
              exclusiveWith.forEach((x) => {
                r[x] = {};
              });
              return r;
            });
          }
        }}
      />
    );
  };

  const ckm = offered.find((track) => track.id === 'ckm');
  const eckm = offered.find((track) => track.id === 'eckm');
  const paired = Boolean(ckm && eckm);
  const rest = paired
    ? offered.filter((track) => track.id !== 'ckm' && track.id !== 'eckm')
    : offered;

  return (
    <div className="rg-screen">
      <div className="rg-inner rg-inner-head">
        <div className="rg-head-block">
          <WregPhase n={2} />
          <h1 className="rg-h1">{gate ? t('wreg.tracks.gate.title') : t('wreg.tracks.title')}</h1>
          <p className="rg-sub">{t('wreg.tracks.sub')}</p>
        </div>
      </div>
      <div className="rg-list-wrap">
        <div className="rg-list">
          <div className="rg-list-inner rg-opts">
            {paired && ckm && eckm && (
              <div className="c-checkopt wreg-trackgroup">
                {rowOf(ckm, ['eckm'])}
                <div className="wreg-trackgroup-div" />
                {rowOf(eckm, ['ckm'])}
              </div>
            )}
            {rest.map((track) => rowOf(track))}
            {disclosure === 'B' && blocked && (
              <div className="wreg-track-off">
                <div className="wreg-track-off-head">
                  <div>
                    <div className="wreg-track-off-t">{t(`wreg.tracks.${blocked}.t`)}</div>
                    <div className="wreg-track-off-d">{t('wreg.tracks.off.status')}</div>
                  </div>
                  <button type="button" className="c-link" onClick={() => setOpenCms(!openCms)}>
                    {openCms ? t('wreg.tracks.off.hide') : t('wreg.tracks.off.more')}
                  </button>
                </div>
                {openCms && <p className="wreg-track-off-cms">{t('wreg.tracks.off.cms')}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="rg-foot">
        <RCta
          disabled={!gate && !picked.length}
          onClick={() =>
            gate && !picked.length
              ? onNoCondition()
              : onContinue(picked.map((track) => track.id), subSel)
          }
        >
          {t('wreg.tracks.cta')}
        </RCta>
      </div>
    </div>
  );
};

export { WregTracks };
